import React, { useState } from "react";
import { MdKeyboardArrowUp } from "react-icons/md";
import { formatBetLine, formatNumber, formatNumbers, getBetMatchupLabel, getBetPrimaryName, getLiveLabel, getPendingMarketSubLabel, isFreePlayBet, isPropsBet } from "../../../helpers/functions.tsx";
import { Timestamp } from "firebase/firestore";
import ImageWithPlaceholder from "../../ImageWithPlaceholder.tsx";
import BetLogo, { DEFAULT_PROPS_LOGO } from "../modals/BetLogo.tsx";
import { formatFiguresDateTime } from "../../../helpers/figuresTime.tsx";
import { MlbPitchersDetailRow } from "../../shared/MlbPitchersDetailRow.tsx";
import "../../../styles/PendingBets.css";

const PendingBetLogo = ({ src, className, usePropsLogo }: { src?: string; className: string; usePropsLogo?: boolean }) => {
    const placeholderClassName = className.includes("sport-logos-pending-player")
        ? "placeholder-logos-pending-player"
        : "placeholder-logos-pending";

    return (
        <ImageWithPlaceholder
            src={usePropsLogo ? DEFAULT_PROPS_LOGO : (src || "")}
            className={className}
            placeholderClassName={placeholderClassName}
            fallbackSrc={DEFAULT_PROPS_LOGO}
            alt="logo"
        />
    );
};

function isOverUnderBet(bet?: { odd?: { selection_line?: string | null } }) {
    const line = String(bet?.odd?.selection_line ?? "").toLowerCase();
    return line === "over" || line.startsWith("over") || line === "under" || line.startsWith("under");
}

const PendingBetContent = ({
    logo,
    logoClassName = "sport-logos-pending-player",
    usePropsLogo,
    bet,
    children,
}: {
    logo?: string;
    logoClassName?: string;
    usePropsLogo?: boolean;
    bet?: { odd?: { selection_line?: string | null }; logo?: string };
    children: React.ReactNode;
}) => (
    <div className="pending-bet-content">
        <div className="pending-bet-logo-wrap">
            {bet && isOverUnderBet(bet) ? (
                <BetLogo bet={bet} className={logoClassName} alt="logo" />
            ) : (
                <PendingBetLogo src={logo} className={logoClassName} usePropsLogo={usePropsLogo} />
            )}
        </div>
        <div className="pending-bet-text">{children}</div>
    </div>
);

const PendingBetComponent = (props: any) => {

    const isParlay = props.betType === "Parlay" || props.betType === "Teaser";
    const status = props.grader?.result ?? props.betStatus;
    const [expandedPlayers, setExpandedPlayers] = useState<Set<string>>(new Set());
    const liveLabel = getLiveLabel(props.gameType);
    const marketSubLabel = getPendingMarketSubLabel(props);
    
    const togglePlayerDetails = (key: string) => {
        setExpandedPlayers(prev => {
            const updated = new Set(prev);
            if (updated.has(key)) {
                updated.delete(key);
            } else {
                updated.add(key);
            }
            return updated;
        });
    };

    const formatDateTime = (timestamp: Timestamp) => {
        if (timestamp && timestamp.seconds !== undefined) {
            return formatFiguresDateTime(new Date(timestamp.seconds * 1000));
        } else {
            return;
        }
    };

    const ticketOdds = formatBetLine(props.odd, props.marketName);
    const ticketDetails = (
        <div className="pending-ticket">
        <div className="pending-ticket__details">
            <div className="pending-ticket__details-head">
                <span>Ticket details</span>
                <span className="pending-ticket__status">{status ? status.charAt(0).toUpperCase() + status.slice(1) : "Pending"}</span>
            </div>
            <div className="pending-ticket__grid">
                <div className="pending-ticket__row">
                    <div><span>Ticket #</span><strong>{props.ticket}</strong></div>
                    <div><span>Sport</span><strong>{props.sport}</strong></div>
                </div>
                <div className="pending-ticket__row">
                    <div><span>Risk</span><strong>${formatNumbers(props.bet)}</strong></div>
                    <div><span>To Win</span><strong>${formatNumbers(props.reward)}</strong></div>
                </div>
                <div className="pending-ticket__row">
                    <div><span>Game</span><strong>{getBetMatchupLabel(props)}</strong></div>
                </div>
                <div className="pending-ticket__row">
                    <div><span>Market</span><strong>{props.marketName}</strong></div>
                    <div><span>Selection</span><strong>{[getBetPrimaryName(props), ticketOdds].filter(Boolean).join(" ")}</strong></div>
                </div>
                <div className="pending-ticket__row">
                    <div><span>Scheduled</span><strong>{formatDateTime(props.gameDate)}</strong></div>
                </div>
                <div className="pending-ticket__row">
                    <div><span>Accepted</span><strong>{formatDateTime(props.betDate)}</strong></div>
                </div>
            </div>
            <button type="button" className="pending-ticket__hide" onClick={() => togglePlayerDetails(props.ticket)}>
                <MdKeyboardArrowUp /> Hide details
            </button>
        </div>
        </div>
    );

    return (
        <div className="bet-info">
            <table className="bet-table " style={{ width: '100%' }}>
                <tbody>
                    {isParlay ? (
                        <>
                            <div className="bet-selection-container cursor" onClick={() => togglePlayerDetails(props.ticket)}>
                                <div className="d-flex align-items-center">
                                    <span className='description-text'>{props.description}</span>
                                </div>
                                <div className="bet-info2">
                                    <span className="money-risk">${formatNumbers(props.bet)}</span>
                                    <span className="money-reward">${formatNumbers(props.reward)}</span>

                                </div>
                            </div>
                            {!expandedPlayers.has(props.ticket) && (
                                <tr>
                                    <td colSpan={8}>
                                        <div className="selection-item">
                                            <div className="selections-container">
                                                {props.selections?.map((selection: any, index: any) => {
                                                    return (
                                                        <div key={index} className="selection-item-details">
                                                            <PendingBetContent logo={selection.logo} bet={selection} usePropsLogo={isPropsBet(selection)}>
                                                                <span className="fw-bold letters-selected">
                                                                    {getBetPrimaryName(selection)}
                                                                </span>
                                                                <span className="body-odds">
                                                                    {selection.marketName?.toLowerCase().includes("moneyline") ? (
                                                                        <>ML {formatNumber(selection.odd?.price, selection.odd?.selection_line)}</>
                                                                    ) : selection.odd?.selection_line?.toLowerCase() === "over" ||
                                                                        selection.odd?.selection_line?.toLowerCase() === "under" ? (
                                                                        <>
                                                                            {selection.odd?.points != null && (
                                                                                <>{formatNumber(selection.odd.points, selection.odd.selection_line, "points")} </>
                                                                            )}
                                                                            {selection.odd?.price != null && formatNumber(selection.odd.price, selection.odd.selection_line)}
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            {selection.odd?.points != null && (
                                                                                <>{formatNumber(selection.odd.points)} </>
                                                                            )}
                                                                            {selection.odd?.price != null && formatNumber(selection.odd.price)}
                                                                        </>
                                                                    )}&nbsp;
                                                                </span>
                                                                {getPendingMarketSubLabel(selection) && (
                                                                    <span className="name-pending-props">
                                                                        {getPendingMarketSubLabel(selection)}
                                                                    </span>
                                                                )}
                                                            </PendingBetContent>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            )}
                            {expandedPlayers.has(props.ticket) && (
                                <tr>
                                    <td className="detail" colSpan={8}>
                                        <div className="pending-parlay">
                                            <div className="pending-parlay__head">
                                                <span>{props.betType === "Teaser" ? "Teaser details" : "Parlay details"}</span>
                                                <span className="pending-ticket__status">{status ? status.charAt(0).toUpperCase() + status.slice(1) : "Pending"}</span>
                                            </div>
                                            <div className="pending-parlay__summary">
                                                <div><span>Ticket #</span><strong>{props.ticket}</strong></div>
                                                <div><span>Risk</span><strong className="pending-parlay__risk">${formatNumbers(props.bet)}</strong></div>
                                                <div><span>To Win</span><strong className="pending-parlay__win">${formatNumbers(props.reward)}</strong></div>
                                                <div><span>Accepted</span><strong>{formatDateTime(props.betDate)}</strong></div>
                                            </div>
                                            <div className="pending-parlay__legs-label">{props.selections?.length ?? 0} legs</div>
                                            {props.selections?.map((selection: any, idx: number) => {
                                                const legOdds = formatBetLine(selection.odd, selection.marketName);
                                                const legStatus = selection.grader?.result ?? selection.betStatus ?? "Pending";
                                                return (
                                                    <div className="pending-parlay__leg" key={selection.ticket ?? idx}>
                                                        <div className="pending-parlay__leg-head">
                                                            <span className="pending-parlay__num">{idx + 1}</span>
                                                            {isOverUnderBet(selection) ? (
                                                                <BetLogo bet={selection} className="sport-logos-pending-player" alt="" />
                                                            ) : (
                                                                <PendingBetLogo src={selection.logo} className="sport-logos-pending-player" usePropsLogo={isPropsBet(selection)} />
                                                            )}
                                                            <span className="pending-parlay__leg-name">
                                                                {getBetPrimaryName(selection)}
                                                                {legOdds ? <span className="pending-ticket__odds"> {legOdds}</span> : null}
                                                            </span>
                                                        </div>
                                                        <div className="pending-parlay__facts">
                                                            <div><span>Ticket #</span><strong>{selection.ticket}</strong></div>
                                                            <div><span>Game</span><strong>{getBetMatchupLabel(selection)}</strong></div>
                                                            <div><span>Sport</span><strong>{selection.sport}</strong></div>
                                                            <div><span>Market</span><strong>{selection.marketName}</strong></div>
                                                            <div><span>Scheduled</span><strong>{formatDateTime(selection.gameDate)}</strong></div>
                                                            <div><span>Status</span><strong>{String(legStatus).charAt(0).toUpperCase() + String(legStatus).slice(1)}</strong></div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            <button type="button" className="pending-ticket__hide" onClick={() => togglePlayerDetails(props.ticket)}>
                                                <MdKeyboardArrowUp /> Hide details
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </>
                    ) : props.betType === "Contest" || isPropsBet(props) || Boolean(marketSubLabel) ? (
                        <>
                            <tr className="cursor">
                                <td colSpan={8}>
                                    <div className="bet-selection-container" onClick={() => togglePlayerDetails(props.ticket)} >
                                        <div className="d-flex align-items-start">
                                            <PendingBetContent logo={props.logo} bet={props} usePropsLogo={isPropsBet(props)}>
                                                <span className="fw-bold">
                                                    {getBetPrimaryName(props)}
                                                    {props.odd && (props.odd.points != null || props.odd.price != null) && (
                                                        <span className="body-odds">
                                                            {props.odd.points != null
                                                                ? formatNumber(props.odd.points, props.odd.selection_line, "points")
                                                                : ""
                                                            }{" "}
                                                            {props.odd.price != null
                                                                ? formatNumber(props.odd.price, props.odd.selection_line, "price")
                                                                : ""
                                                            }
                                                        </span>
                                                    )}
                                                    {liveLabel && ` (${liveLabel})`}
                                                    {isFreePlayBet(props) && (
                                                        <span className="bet-free">
                                                            {props.freeBet}
                                                        </span>
                                                    )}
                                                </span>
                                                {marketSubLabel && (
                                                    <span className='name-pending-props'>
                                                        {marketSubLabel}
                                                    </span>
                                                )}
                                            </PendingBetContent>
                                        </div>
                                        <div className="bet-info2">
                                            <span className="money-risk">
                                                ${formatNumbers(props.bet)}
                                            </span>
                                            <span className="money-reward">
                                                ${formatNumbers(props.reward)}
                                            </span>
                                        </div>
                                    </div>
                                </td>
                            </tr>

                            {expandedPlayers.has(props.ticket) && (
                                <tr>
                                    <td className="detail">{ticketDetails}</td>
                                </tr>
                            )}
                        </>
                    ) : (
                        <>
                            <tr className="cursor">
                                <td colSpan={8}>
                                    <div className="bet-selection-container" onClick={() => togglePlayerDetails(props.ticket)}>
                                        <div className="d-flex align-items-start">
                                            <PendingBetContent logo={props.logo} bet={props} usePropsLogo={isPropsBet(props)}>
                                                <span className="fw-bold letters-selected">
                                                    {getBetPrimaryName(props)}
                                                    {props.gameType && ` (${props.gameType.charAt(0).toUpperCase()}${props.gameType.slice(1)})`}
                                                </span>
                                                {props.odd && (props.odd.price != null || props.odd.points != null) && (
                                                    <span className="body-odds">
                                                        {props.marketName?.toLowerCase().includes("moneyline") ? (
                                                            `ML ${formatNumber(props.odd.price, props.odd.selection_line)}`
                                                        ) : (
                                                            <>
                                                                {props.odd.points != null && formatNumber(props.odd.points, props.odd.selection_line, "points")}{" "}
                                                                {props.odd.price != null && formatNumber(props.odd.price, props.odd.selection_line, "price")}
                                                            </>
                                                        )}&nbsp;
                                                    </span>
                                                )}
                                                {isFreePlayBet(props) && (
                                                    <span className="bet-free">
                                                        {props.freeBet}
                                                    </span>
                                                )}
                                            </PendingBetContent>
                                        </div>
                                        <div className="bet-info2">
                                            <span className="money-risk">${formatNumbers(props.bet)}</span>
                                            <span className="money-reward">${formatNumbers(props.reward)}</span>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                            {expandedPlayers.has(props.ticket) && (
                                <tr>
                                    <td className="detail">
                                        {ticketDetails}
                                        <MlbPitchersDetailRow pitchers={props.pitchers} />
                                    </td>
                                </tr>
                            )}
                        </>
                    )}
                </tbody>
            </table>
        </div>
    );


};

export default PendingBetComponent;